import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { en, TranslationKeys } from './translations/en';
import { ta } from './translations/ta';

export type Language = 'en' | 'ta';

// Type for the translation function
type TranslationFunction = (key: string) => string;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationFunction;
  translations: TranslationKeys;
}

const LANGUAGE_STORAGE_KEY = 'devi-impex-language';

const translationsMap: Record<Language, TranslationKeys> = {
  en,
  ta,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper function to get nested value from object using dot notation
const getNestedValue = (obj: any, path: string): string => {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current === undefined || current === null) {
      return path; // Return the key if path not found
    }
    current = current[key];
  }
  return typeof current === 'string' ? current : path;
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get saved language from localStorage or default to English
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved === 'en' || saved === 'ta') {
      return saved;
    }
    return 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  };

  // Update document language attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Translation function that looks up keys with dot notation support
  const t = useCallback((key: string): string => {
    return getNestedValue(translationsMap[language], key);
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    translations: translationsMap[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Also export useLanguage as an alias for useTranslation for compatibility
export const useLanguage = useTranslation;

// Helper function for string interpolation
// Usage: interpolate(t('home.kgRemaining'), { value: '5.5' })
export const interpolate = (text: string, values: Record<string, string | number>): string => {
  return Object.entries(values).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
  }, text);
};

